import { Stack, Typography } from "@mui/material";
import { FC } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Credential } from "../../graphql/types";
import { dummyPassword, snakeCaseToCamelCase } from "../../lib/Util";
import { ControlledRadioButton, IControlledRadioButton } from "./ControlledRadioButton";
import { ControlledTextField, IControlledTextField } from "./ControlledTextField";
import { KeyCell } from "./KeyCell";
import { INPUT_PLACEHOLDERS } from "./KeyInputPlaceholders";

interface IFormController<T extends FieldValues> {
  elementType: ElementType;
  type: string;
  fieldName: Path<T>;
  label: string;
  rules?: RegisterOptions<T, Path<T>>;
  control?: Control<T, unknown>;
  errors?: FieldErrors<T>;
  mode?: string;
  textAreaHeight?: string;
  options?: Array<{
    value: string;
    label: string;
  }>;
  setValue?: UseFormSetValue<
    | T
    | {
        key_name: string;
      }
  >;
  credential?: Credential;
  watch?: UseFormWatch<T>;
  getValues?: UseFormGetValues<T>;
  editingField?: string;
  setEditingField?: (e: string) => void;
  loading?: boolean;
  placeholder?: string;
  testid?: string;
}

type ElementType = "textField" | "radioButton" | "textarea";

// The render below passes a props object that satisfies both shapes, so the map entries accept
// their intersection — a union would mean each component accepts the other's props, which is false.
type FormFieldProps<T extends FieldValues> = IControlledTextField<T> & IControlledRadioButton;

const FormController = <T extends FieldValues>({
  elementType,
  type,
  fieldName,
  label,
  rules,
  control,
  errors,
  mode,
  options,
  setValue,
  credential,
  editingField,
  setEditingField,
  loading,
  textAreaHeight,
  placeholder,
  testid,
}: IFormController<T>) => {
  const required = Boolean(rules?.required);
  const ElementTypeMap: Record<ElementType, FC<FormFieldProps<T>>> = {
    textField: ControlledTextField,
    radioButton: ControlledRadioButton,
    textarea: ControlledTextField,
  };
  const Field: FC<FormFieldProps<T>> = ElementTypeMap[elementType];

  // When an user is editing, they do not have the password fields' values. But we still want to be able to submit a query with an empty password field, which means the user is not changing the password. The information will be consistent since the password is already stored in the database.
  const resolveRequired = type === "password" && mode === "edit" ? false : required;
  const updatedRules = { ...rules, required: resolveRequired };

  const fieldNameCamelcased = snakeCaseToCamelCase(fieldName);

  const resolvePlaceholder = (value: unknown) => {
    if (type === "password" && !value && credential?.properties_exist[fieldName]) return dummyPassword;

    return placeholder || (fieldNameCamelcased in INPUT_PLACEHOLDERS ? INPUT_PLACEHOLDERS[fieldNameCamelcased] : "");
  };

  return (
    <Controller<T>
      key={fieldName}
      name={fieldName}
      control={control}
      rules={updatedRules}
      render={({ field: { onChange, value } }) => {
        if (mode === "view") {
          return (
            <KeyCell
              label={label}
              value={value || (credential?.properties_exist[fieldName] ? dummyPassword : undefined)}
              allowCopyValue={fieldName === "key_name"}
            />
          );
        }
        return (
          <Stack data-testid="form-controller" spacing={1} sx={{ height: "100%" }}>
            <Typography variant="body.medium.semibold" sx={{ marginBottom: "4px" }}>
              {`${label}${required ? " *" : ""}`}
            </Typography>
            <Field
              value={value || ""}
              onChange={onChange}
              errors={errors}
              required={resolveRequired}
              fieldName={fieldName}
              label={label}
              mode={mode}
              type={type}
              options={options}
              editingField={editingField}
              setEditingField={setEditingField}
              setValue={setValue}
              credential={credential}
              isTextArea={elementType === "textarea"}
              loading={loading}
              textAreaHeight={textAreaHeight}
              placeholder={resolvePlaceholder(value)}
              testid={testid}
            />
          </Stack>
        );
      }}
    />
  );
};

export { FormController, IFormController };
